/* ============================================================
   STROVIA — Articles / Journal carousel

   Progressive enhancement over a native scroll-snap track: without this file
   the section is still a fully usable horizontal scroller. What it adds:

     - prev/next buttons that step one card and disable at the ends
     - a proportional progress rail (thumb width = viewport / content)
     - drag-to-scroll with the mouse, with snap suspended mid-drag and a
       deterministic re-snap on release (browsers disagree on re-snapping)
     - click suppression after a drag, so a drag never opens an article
     - arrow / Home / End keys on the focused track
     - optional autoplay, which stops for good on the first real interaction
       and never runs under prefers-reduced-motion

   Written in the same ES5-ish style as assets/strovia.js. Every listener is
   registered against an AbortController so `shopify:section:unload` in the
   theme editor leaves nothing behind.
   ============================================================ */
window.StroviaArticles = (function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function behavior() {
    return reduceMotion.matches ? 'auto' : 'smooth';
  }

  function setup(carousel) {
    /* Idempotent: re-running init on an already-wired carousel is a no-op. */
    if (carousel._ja) return carousel._ja;

    var track = carousel.querySelector('[data-ja-track]');
    if (!track) return null;

    /* Controls live in the section head AND under the track (one set is hidden
       per breakpoint), so they are looked up on the section, not the carousel. */
    var scope = carousel.closest('.strovia-articles') || carousel;
    var prevBtns = scope.querySelectorAll('[data-ja-prev]');
    var nextBtns = scope.querySelectorAll('[data-ja-next]');
    var rail = carousel.querySelector('[data-ja-progress]');
    var bar = carousel.querySelector('[data-ja-bar]');

    var delay = parseInt(carousel.getAttribute('data-ja-autoplay'), 10) || 0;
    var ac = new AbortController();
    var opts = { signal: ac.signal };
    var passive = { passive: true, signal: ac.signal };

    var timer = null;
    var engaged = false;
    var rafId = 0;
    var settleTimer = null;
    /* Where a programmatic scroll is heading. Needed because scrollLeft reports
       the CURRENT position mid-animation: without it, two quick taps on Next
       both compute "current + one card" and together advance barely more than
       one card. Cleared on arrival and on any user-initiated scroll. */
    var target = null;

    /* ---------- Geometry ---------- */
    function stepSize() {
      var item = track.querySelector('.ja-item');
      if (!item) return track.clientWidth;
      var cs = window.getComputedStyle(track);
      var gap = parseFloat(cs.columnGap);
      if (isNaN(gap)) gap = parseFloat(cs.gap);
      if (isNaN(gap)) gap = 0;
      return item.getBoundingClientRect().width + gap;
    }

    function maxScroll() {
      return Math.max(track.scrollWidth - track.clientWidth, 0);
    }

    /* ---------- Render state ---------- */
    function update() {
      var max = maxScroll();
      var x = track.scrollLeft;
      var i;

      if (target !== null) {
        if (Math.abs(x - target) < 2) releaseTarget();
        else armSettle();
      }

      /* Nothing to scroll (few articles on a wide screen) — the arrows and the
         rail would be dead controls, so drop them out of the layout entirely. */
      scope.classList.toggle('is-static', max <= 1);

      /* Buttons follow the DESTINATION, not the live position: clicking Next
         onto the last card has to disable it at once, not a beat later when
         the smooth scroll finally lands. */
      var aim = target === null ? x : target;
      for (i = 0; i < prevBtns.length; i++) prevBtns[i].disabled = aim <= 1;
      for (i = 0; i < nextBtns.length; i++) nextBtns[i].disabled = aim >= max - 1;

      if (!rail || !bar) return;
      var railW = rail.clientWidth;
      if (!railW || !track.scrollWidth) return;
      var ratio = Math.min(track.clientWidth / track.scrollWidth, 1);
      var thumb = Math.max(railW * ratio, 24);
      var travel = max > 0 ? (railW - thumb) * (x / max) : 0;
      bar.style.width = thumb + 'px';
      bar.style.transform = 'translateX(' + travel + 'px)';
    }

    /* Cancel-and-replace rather than a `ticking` boolean: a flag that is set
       before the frame runs stays stuck forever if that frame never arrives,
       and a stuck flag silently freezes the arrows and the progress rail. */
    function scheduleUpdate() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        rafId = 0;
        update();
      });
    }

    /* ---------- Movement ---------- */
    function scrollToX(x) {
      var max = maxScroll();
      target = Math.max(0, Math.min(x, max));
      track.scrollTo({ left: target, behavior: behavior() });
      /* Synchronous, not scheduleUpdate: the arrows have to settle on the same
         tick as the click that moved them. */
      update();
    }

    function go(dir, wrap) {
      var max = maxScroll();
      var from = target === null ? track.scrollLeft : target;
      var to = from + dir * stepSize();
      if (wrap && dir > 0 && from >= max - 1) to = 0;
      scrollToX(to);
    }

    function snapToNearest() {
      var s = stepSize();
      if (!s) return;
      scrollToX(Math.round(track.scrollLeft / s) * s);
    }

    /* Any hand-driven scroll invalidates the programmatic destination. */
    function releaseTarget() {
      target = null;
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    }

    /* Backstop for a programmatic scroll that never reaches its destination —
       interrupted by a wheel, a drag, or a resize mid-animation. Once scroll
       events stop arriving, whatever we were aiming at is no longer true. */
    function armSettle() {
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        settleTimer = null;
        target = null;
      }, 260);
    }

    /* ---------- Autoplay ---------- */
    function stopAuto() {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    }

    function startAuto() {
      stopAuto();
      if (!delay || engaged || reduceMotion.matches) return;
      if (maxScroll() <= 1) return;
      timer = setInterval(function () {
        if (document.hidden) return;
        go(1, true);
      }, delay);
    }

    /* The first deliberate interaction ends autoplay permanently — resuming
       under someone who has taken control is the part people find hostile. */
    function engage() {
      engaged = true;
      stopAuto();
    }

    /* ---------- Wiring ---------- */
    var i;
    for (i = 0; i < prevBtns.length; i++) {
      prevBtns[i].addEventListener('click', function () {
        engage();
        go(-1);
      }, opts);
    }
    for (i = 0; i < nextBtns.length; i++) {
      nextBtns[i].addEventListener('click', function () {
        engage();
        go(1);
      }, opts);
    }

    track.addEventListener('scroll', scheduleUpdate, passive);
    track.addEventListener('wheel', releaseTarget, passive);
    track.addEventListener('touchstart', releaseTarget, passive);

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        engage();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        engage();
        go(-1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        engage();
        scrollToX(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        engage();
        scrollToX(maxScroll());
      }
    }, opts);

    /* ---------- Drag to scroll (mouse / pen only) ----------
       Touch is left to the browser: native momentum + snap already beats
       anything re-implemented here, and hijacking it breaks vertical scroll. */
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var travelled = 0;
    var suppressClick = false;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      releaseTarget();
      dragging = true;
      travelled = 0;
      suppressClick = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    }, opts);

    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      travelled = Math.max(travelled, Math.abs(dx));
      if (travelled > 3) e.preventDefault();
      track.scrollLeft = startScroll - dx;
    }, { passive: false, signal: ac.signal });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      /* A real drag was in progress — swallow the click it is about to fire so
         releasing over a card does not navigate to that article. */
      if (travelled > 5) {
        suppressClick = true;
        engage();
        snapToNearest();
      }
    }

    window.addEventListener('pointerup', endDrag, opts);
    window.addEventListener('pointercancel', endDrag, opts);

    track.addEventListener('click', function (e) {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    }, { capture: true, signal: ac.signal });

    track.addEventListener('dragstart', function (e) {
      if (dragging) e.preventDefault();
    }, opts);

    /* ---------- Autoplay pausing ---------- */
    if (delay) {
      carousel.addEventListener('pointerenter', stopAuto, opts);
      carousel.addEventListener('pointerleave', startAuto, opts);
      carousel.addEventListener('focusin', stopAuto, opts);
      carousel.addEventListener('focusout', startAuto, opts);
      carousel.addEventListener('touchstart', engage, passive);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) stopAuto();
        else startAuto();
      }, opts);
    }

    /* ---------- Reflow ---------- */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(scheduleUpdate);
      ro.observe(track);
      if (rail) ro.observe(rail);
      ac.signal.addEventListener('abort', function () {
        ro.disconnect();
      });
    } else {
      window.addEventListener('resize', scheduleUpdate, passive);
    }

    /* Late-loading images change scrollWidth after the first measurement. */
    var imgs = track.querySelectorAll('img');
    for (i = 0; i < imgs.length; i++) {
      if (!imgs[i].complete) imgs[i].addEventListener('load', scheduleUpdate, passive);
    }

    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', function () {
        if (reduceMotion.matches) stopAuto();
        else startAuto();
      }, opts);
    }

    var api = {
      update: update,
      next: function () { go(1); },
      prev: function () { go(-1); },
      destroy: function () {
        stopAuto();
        releaseTarget();
        if (rafId) cancelAnimationFrame(rafId);
        rafId = 0;
        ac.abort();
        delete carousel._ja;
      }
    };

    carousel._ja = api;
    update();
    startAuto();
    return api;
  }

  function init(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-ja-carousel]');
    for (var i = 0; i < nodes.length; i++) setup(nodes[i]);
  }

  function destroy(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-ja-carousel]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i]._ja) nodes[i]._ja.destroy();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  /* Theme editor: sections are re-rendered in place. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    destroy(e.target);
  });

  return { init: init, destroy: destroy };
})();
